<template>
  <frontoffice-frame class="home-form">
    <form-base v-if="isLoaded"
               type="home_research"
               ref="homeSearch"
               page-title="home.form.title"
               :form-data="formData"
               :display-grid="true"
               :default-submit-button="false"
               :callbacks="submitCallbacks"
               :server-validation="false"
               :invoked-submit-button-name="invokedSubmitButtonName"
               @submit.prevent=""
    >
      <input type="hidden"
             name="invokedSubmitButtonName"
             ref="invokedSubmitButtonName"
             :value="invokedSubmitButtonName"
      />

      <b-row class="justify-content-center">
        <b-col class="custom-input-section"
               cols="12" md="6" order="1" order-md="1" lg="4" xl="4" order-lg="1"
        >
          <locationAutocomplete class="destination"
                                label="home.form.destination.label"
                                placeholder="home.form.destination.placeholder"
                                v-model="formData.destination"
                                ref="destination"
                                :display-grid="false"
                                :required="false"
                                :deselect="true"
          />
        </b-col>

        <b-col cols="12" md="6" order="3" order-md="2" lg="6" order-lg="4"
               class="custom-input-section"
        >
          <bedrooms-repartition class="bedrooms" label="home.form.bedrooms.label"
                                v-model="formData.bedrooms" ref="bedrooms"
          />
        </b-col>

        <b-col v-if="displayFavorites" class="custom-input-section"
               cols="12" order="5" md="6" offset-md="1" lg="6" offset-lg="0" order-lg="5"
        >
          <favorite-selection v-model="formData.favorite" ref="favorite"
                              :favorite-locations="favoriteLocations" :deselect="true"
          />
        </b-col>

        <date-range class="dates" v-model="formData.dates" :home-page="true" />
      </b-row>

      <b-row v-if="onlineEnabled && offlineEnabled"
             class="justify-content-center"
      >
        <b-col cols="12" md="6" class="carry-on">
          <span class="text">
            {{ $t('home.carry_on.choose_one_booking_type') }}
          </span>
        </b-col>
      </b-row>

      <b-row v-if="!onlineEnabled && !offlineEnabled"
             class="no-access input-container justify-content-center"
      >
        <p class="col-12 col-md-10">
          {{ $t('_no_booking_access.phrase') }}

          <router-link :to="{name: 'home_message_create'}">
            {{ $t('_no_booking_access.contact') }}
          </router-link>
        </p>
      </b-row>

      <b-row v-else class="submitForm justify-content-center justify-content-md-around">
        <div v-if="displayOnlineSubmitButton"
             id="autonomously-tooltip"
             class="col-6 col-md-4 col-lg-3 p-1 autonomously-msg"
        >
          <input type="button"
                 :value="$t('home.online.online')"
                 class="submitForm__autonomously btn orange-white width-max"
                 name="autonomously"
                 v-on:click="onInvokedSubmitButton"
          />

          <b-tooltip target="autonomously-tooltip">
            {{$t('home.online.tooltip_message')}}
          </b-tooltip>
        </div>

        <div v-if="displayOfflineSubmitButton"
             id="with-agent-tooltip"
             class="col-6 col-md-4 col-lg-3 p-1 with-agent-msg"
        >
          <input type="button"
                 :value="$t('home.offline.offline')"
                 class="submitForm__with-agent btn orange-white width-max"
                 name="with-agent"
                 v-on:click="onInvokedSubmitButton"
          />

          <b-tooltip target="with-agent-tooltip">
            {{$t('home.offline.tooltip_message')}}
          </b-tooltip>
        </div>
      </b-row>
    </form-base>
  </frontoffice-frame>
</template>

<script>
import frontofficeFrame from '@/components/frontoffice/frame'
import formBase from '@/components/frontoffice/form/base'
import favoriteSelection from '@/components/frontoffice/form/favoriteSelection'
import bedroomsRepartition from '@/components/frontoffice/form/bedroomsRepartition'
import dateRange from '@/components/frontoffice/form/baseInput/dateRange'
import dateRangeMixin from '@/components/frontoffice/mixins/dateRange'
import submitButton from '@/components/frontoffice/form/baseInput/submit'
import locationAutocomplete from '@/components/frontoffice/form/locationAutocomplete'
import CommonApi from '@/api/commonApi'
import { formatDateForOnlineRequest } from '@/utils/date'

export default {
  components: {
    frontofficeFrame,
    formBase,
    favoriteSelection,
    bedroomsRepartition,
    dateRange,
    submitButton,
    locationAutocomplete
  },

  mixins: [dateRangeMixin],

  data () {
    return {
      formData: null,
      submitCallbacks: {
        autonomously: this.onAutonomously,
        'with-agent': this.onWithAgent
      },
      invokedSubmitButtonName: '',
      isLoaded: false,
      onlineEnabled: false,
      offlineEnabled: false,
      maxTravellers: null,
      commonApi: new CommonApi(),
      displayHotel: false,
      displayFavorites: false,
      favoriteLocations: []
    }
  },

  created () {
    this.commonApi.companyFavoriteLocations()
      .then(response => {
        this.favoriteLocations = response.data.data
        this.displayFavorites = this.favoriteLocations.length > 0
        this.isLoaded = true
      })

    this.setFormData()
    this.user = this.$store.getters['security/user']
    this.company = this.$store.getters['security/company']
    this.onlineEnabled = this.user.online
    this.offlineEnabled = this.user.offline
    this.maxTravellers = this.commonApi.bookingMaxTravellers()
  },

  methods: {
    onInvokedSubmitButton (event) {
      this.invokedSubmitButtonName = event.target.name
      this.$refs.homeSearch.dataSubmit(this.invokedSubmitButtonName)
    },

    onAutonomously () {
      this.hydrateStore(true)
      this.$router.push({ name: 'online_results' })
    },

    onWithAgent () {
      this.hydrateStore()
      this.$router.push({ name: 'offline_home' })
    },

    hydrateStore (onAutonomously = false) {
      for (let key in this.formData) {
        if (this.formData.hasOwnProperty(key)) {
          if (key === 'services') {
            let storeData = this.$store.state.research.services
            let allKeys = Object.keys(storeData)

            for (let k of allKeys) {
              let val = ''

              if (this.formData[key].hasOwnProperty(k)) {
                val = this.formData[key][k].selected
              } else {
                val = false
              }

              this.$store.commit('research/' + k, val)
            }
          } else if (key === 'dates') {
            let from = this.formData[key]['from']
            let to = this.formData[key]['to']

            if (onAutonomously) {
              from = formatDateForOnlineRequest(from)
              to = formatDateForOnlineRequest(to)
            }

            this.$store.commit('research/dates', {
              from: from,
              to: to
            })
          } else {
            this.$store.commit('research/' + key, this.formData[key])
          }
        }
      }
      this.$store.commit('research/resetFilters')
    },

    setFormData () {
      let search = this.$store.getters['research/form']

      let destination = search.destination && search.destination.locationId
        ? search.destination
        : null

      this.formData = {
        destination: destination,
        favorite: {
          location: null,
          hotel: null
        },
        bedrooms: search.bedrooms,
        dates: search.dates,
        services: search.services
      }
    },

    displaySubmitButton (type) {
      let enabled = false

      switch (type) {
        case 'offline': enabled = this.offlineEnabled; break
        case 'online': enabled = this.onlineEnabled; break
      }

      let single = this.formData.bedrooms.single.amount
      let double = this.formData.bedrooms.double.amount
      let max = this.maxTravellers

      return enabled && (single <= max) && (double <= max) && (single + double <= max)
    }
  },

  computed: {
    displayOnlineSubmitButton () {
      return this.displaySubmitButton('online')
    },

    displayOfflineSubmitButton () {
      return this.displaySubmitButton('offline')
    }
  }
}
</script>
